import { useState, useEffect } from "react"
import { useForm } from "@inertiajs/react"
import { IconCapture, IconTrash } from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useIsMobileShell } from "@/hooks/use-is-mobile-shell"
import { CalibrationCaptureSheet } from "./calibration-capture-sheet"
import { CalibrationTable } from "./calibration-table"
import type { CalibrationPoint, SensorReading, Device } from "@/types/device"

interface CalibrationPanelProps {
  device: Device
  reading: SensorReading
  points: CalibrationPoint[]
  onAddPoint: (rawSg: number, referenceSg: number, temperature: number) => void
  onRemovePoint: (index: number) => void
  onClearPoints: () => void
  isConnected: boolean
}

export function CalibrationPanel({
  device,
  reading,
  points,
  onAddPoint,
  onRemovePoint,
  onClearPoints,
  isConnected,
}: CalibrationPanelProps) {
  const isMobile = useIsMobileShell()
  const [referenceSg, setReferenceSg] = useState("")

  const form = useForm({
    calibration_data: JSON.stringify(points),
  })

  // Keep form data in sync with local points state
  useEffect(() => {
    form.setData("calibration_data", JSON.stringify(points))
  }, [points])

  // Save calibration data
  const handleSave = () => {
    form.patch(`/devices/${device.id}`, {
      preserveScroll: true,
      only: ["device"],
    })
  }

  const canCapture =
    isConnected &&
    reading.specificGravity !== null &&
    reading.temperature !== null &&
    referenceSg.trim() !== ""

  const handleCapture = () => {
    if (!canCapture) return

    const refSg = parseFloat(referenceSg)
    if (isNaN(refSg) || refSg <= 0) return

    onAddPoint(reading.specificGravity!, refSg, reading.temperature!)
    setReferenceSg("")
  }

  const handleClear = () => {
    if (confirm("Are you sure you want to clear all calibration points?")) {
      onClearPoints()
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-medium">Multi-Point Calibration</h3>
        <p className="text-sm text-muted-foreground">
          Calibrate your hydrometer by capturing readings at known specific
          gravity values
        </p>
      </div>

      {/* Capture Section: drawer on mobile, inline grid on desktop */}
      {isMobile ? (
        <CalibrationCaptureSheet
          reading={reading}
          points={points}
          onAddPoint={onAddPoint}
        />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Capture Calibration Point</CardTitle>
            <CardDescription>
              Enter the known reference SG and capture the current device reading
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-[1fr_auto_auto_auto] sm:items-end">
              <div className="grid gap-2">
                <Label htmlFor="reference-sg">Reference SG (known value)</Label>
                <Input
                  id="reference-sg"
                  type="number"
                  step="0.001"
                  min="0.990"
                  max="1.200"
                  placeholder="1.050"
                  value={referenceSg}
                  onChange={(e) => setReferenceSg(e.target.value)}
                  disabled={!isConnected}
                />
              </div>
              <div className="grid grid-cols-2 gap-4 sm:contents">
                <div className="grid gap-2">
                  <Label className="text-muted-foreground">Current Raw SG</Label>
                  <div className="h-9 px-3 flex items-center border rounded-md bg-muted/50 tabular-nums">
                    {reading.specificGravity?.toFixed(4) ?? "--"}
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label className="text-muted-foreground">Current Temp</Label>
                  <div className="h-9 px-3 flex items-center border rounded-md bg-muted/50 tabular-nums">
                    {reading.temperature?.toFixed(1) ?? "--"}°
                  </div>
                </div>
              </div>
              <Button onClick={handleCapture} disabled={!canCapture} className="w-full sm:w-auto">
                <IconCapture className="size-4" />
                Capture
              </Button>
            </div>
            {!isConnected && (
              <p className="mt-2 text-sm text-muted-foreground">
                Connect to your device and enable streaming to capture calibration
                points
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Calibration Points Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Calibration Points</CardTitle>
              <CardDescription>
                {points.length} point{points.length !== 1 ? "s" : ""} captured
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {form.recentlySuccessful && (
                <span className="text-sm text-green-600">Saved!</span>
              )}
              {points.length > 0 && (
                <>
                  <Button variant="outline" size="sm" onClick={handleClear}>
                    <IconTrash className="size-4" />
                    Clear All
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={form.processing}
                  >
                    {form.processing ? "Saving..." : "Save Calibration"}
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <CalibrationTable points={points} onRemove={onRemovePoint} />
        </CardContent>
      </Card>
    </div>
  )
}
